const GUIDES = {
    general: {
        title: 'General',
        intro: 'Rules every center follows, unless a center sets its own value in Center Settings.',
        steps: [
            'Booking rules: set how long one slot is, how many people can book it, and whether same-day booking is allowed.',
            'Weekly schedule: the strip shows all 7 days. Click a day, then pick one choice — Open all day, Closed every week, Closed for some dates, or Open at set times.',
            'Fill in the dates or times the choice asks for. "What customers get" shows the result on a 24-hour bar and in one sentence.',
            'Same setting for several days? Set one day, then use Copy (e.g. Weekdays) to apply it to the others.',
            'Click Save changes. Unsaved changes are marked at the bottom; Discard undoes them.'
        ],
        examples: [
            { task: 'Every Sunday off', how: 'Click Sun → Closed every week' },
            { task: 'Wednesday off until 31 Dec', how: 'Click Wed → Closed for some dates → Closed from today, Closed until 31 Dec' },
            { task: 'Friday with a break', how: 'Click Fri → Open at set times → 07:00 AM to 12:00 PM → Add a break → 09:00 AM to 10:00 AM' },
            { task: 'Same hours Monday to Friday', how: 'Set Monday → Copy → Weekdays → Copy' }
        ],
        note: 'Changing slot duration or seats here also changes the slots of every center that does not set its own value. After saving, the slot changes are listed at the top of the page.'
    },
    center: {
        title: 'Center Settings',
        intro: 'Give one center its own rules. Anything you do not change keeps following General.',
        steps: [
            'Pick a center. "Follows General settings" means it has no own rules yet.',
            'Booking rules: switch off "Use General" next to a rule and enter this center\'s own value.',
            'Weekly schedule: days marked "General" follow General. Click a day, switch off "Same as General", then pick a choice just for this center.',
            'Check the Effective values card: it shows what customers of this center really get.',
            'Click Save changes. Reset to General removes all of this center\'s own rules.'
        ],
        examples: [
            { task: 'This center works Sundays 9 AM–1 PM', how: 'Click Sun → switch off Same as General → Open at set times → 09:00 AM to 01:00 PM' },
            { task: 'More seats at this center', how: 'Seats per slot → switch off Use General → enter the number' }
        ],
        note: 'Holidays and special days are not changed here; use the Holidays tab.'
    },
    counters: {
        title: 'Counters',
        intro: 'Each counter is one seat. The seats at a time = the active counters open at that time.',
        steps: [
            'Pick a center and click Add counter.',
            'Give it a name (e.g. M1), choose who it serves — Male, Female or Both — and set its start and end time.',
            'Add a break if the counter closes for a while; it gives no seat during the break.',
            'Save. Seats are recalculated right away; the slot changes are listed at the top of the page.',
            'Check the Seats row under the timeline: how many people can book each slot.',
            'Use the Active switch to pause a counter, Edit to change it, Delete to remove it.'
        ],
        examples: [
            { task: '3 people at a time between 10:00 and 10:30', how: 'Have 3 active counters open 10:00–10:30 (e.g. add M2 and M3)' },
            { task: '2 male and 1 female counter', how: 'Add M1 (Male, 08:00 AM – 04:00 PM, break 12–1 PM), M2 (Male, 08:00 AM – 12:00 PM), F1 (Female, 09:00 AM – 02:00 PM)' },
            { task: 'A counter for anyone', how: 'Add counter → Who can book: Both (a seat for either gender)' },
            { task: 'One counter less in the afternoon', how: 'Edit that counter → End time 01:00 PM → Save' }
        ],
        note: 'A counter counts for a slot only when it is open for the whole slot and not on break. Existing bookings are never moved or cancelled; if seats drop below the bookings already made, those bookings stay. To close the whole center on a day, use Holidays.'
    },
    holidays: {
        title: 'Holidays & Special Days',
        intro: 'Close or open centers on specific dates, for all centers or one center.',
        steps: [
            'Click Add holiday to close, or Add special opening to open on a day that is normally closed.',
            'Pick All centers or one center, enter a title and pick the dates.',
            'Switch off Whole day to close or open only between two times.',
            'Switch on Repeat every year for holidays on the same date every year.',
            'Use the filters to find entries; edit or delete them from the table.'
        ],
        examples: [
            { task: 'UAE National Day', how: 'Add holiday → All centers → 2 Dec to 3 Dec → Repeat every year' },
            { task: 'Eid (date changes every year)', how: 'Add holiday → All centers → this year\'s dates, Repeat off' },
            { task: 'One center open on a public holiday', how: 'Add special opening → that center → that date' },
            { task: 'One center closed one afternoon', how: 'Add holiday → that center → date → Whole day off → 02:00 PM to 06:00 PM' }
        ],
        note: 'A center entry wins over an All centers entry on the same date. Existing bookings are never cancelled automatically; you get a warning if a holiday falls on confirmed bookings.'
    },
    calendar: {
        title: 'Calendar',
        intro: 'Month view of what customers of one center can book, after all rules and holidays.',
        steps: [
            'Pick a center and a month (use the arrows to move between months).',
            'Read the colours: green = open, grey = weekly off, red = holiday, light = not bookable (past, too far ahead, same day).',
            'Click a future date to change it: an open day becomes a holiday, a closed day a special opening.'
        ],
        examples: [
            { task: 'Check that a holiday is in place', how: 'Pick the center and month → the date shows red with the holiday title' }
        ],
        note: 'Past dates cannot be changed.'
    },
    slots: {
        title: 'Slot Preview',
        intro: 'Shows the exact slots customers see for a center and date, using the live slots API.',
        steps: [
            'Pick a center and a date (or use Today, Tomorrow, In 7 days, In 30 days).',
            'At centers with counters, pick the Applicant (Male / Female): seats then count only the counters they can use.',
            'Read the banner: it says whether the day is open, and if not, why.',
            'Check each slot: time, how many seats are booked and how many are free. At counter centers the seats = counters open at that time.'
        ],
        examples: [
            { task: 'Did my new Friday hours work?', how: 'Pick a Friday → only slots inside the set hours are listed' },
            { task: 'How many women can book at 9 AM?', how: 'Applicant: Female applicant → the 9:00 AM slot shows the Female + Both counters as seats' }
        ],
        note: 'Fully booked slots are not returned by the slots API, so they do not appear here.'
    }
};

export default GUIDES;
